import React from "react";
import { useNavigate } from "react-router-dom";
import { TwMainLayout } from "../../components/tailwind";
import {
  Alert, Card, ClubContact, Icons, PrimaryButton, SessionPicker, SessionSummary, TrialLayout,
  dayKey, downloadIcs, longDay, mapsUrl, timeText, trialApi, useTrialClub,
} from "./trialShared";

/**
 * /trial/manage?c=<code> — the link in the trial SMS / email. See the trial, cancel it, or pick another time.
 * POST trialManage {c} / trialCancel {c} / trialReschedule {c, scheduleClassId}
 */
export default function TrialManagePage() {
  const navigate = useNavigate();
  const { link, details } = useTrialClub();
  const code = React.useMemo(() => new URLSearchParams(window.location.search).get("c") || "", []);

  // loading | error | view | pick | confirmMove | confirmCancel | moved | cancelled
  const [phase, setPhase] = React.useState(code ? "loading" : "error");
  const [error, setError] = React.useState(code ? "" : "This link isn't complete. Use the link in the text message we sent you.");
  const [info, setInfo] = React.useState(null);
  const [target, setTarget] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [actionError, setActionError] = React.useState("");

  const load = React.useCallback(async () => {
    const r = await trialApi("post", "trialManage", { c: code });
    if (!r.ok) { setError(r.message); setPhase("error"); return; }
    setInfo({ ...r.data, message: r.message });
    setPhase("view");
  }, [code]);

  React.useEffect(() => { if (code) load(); }, [code, load]);
  React.useEffect(() => { window.scrollTo(0, 0); }, [phase]);

  const club = info?.club;
  const clubName = club?.name || details?.business_detail?.businessName || "the club";
  const clubLink = link || club?.myBookingLink || "";
  const trial = info?.trial;

  const cancel = async () => {
    setBusy(true); setActionError("");
    const r = await trialApi("post", "trialCancel", { c: code });
    setBusy(false);
    if (!r.ok) { setActionError(r.message); return; }
    setInfo((x) => ({ ...x, trial: null, canBook: r.data?.canBook }));
    setPhase("cancelled");
  };

  const move = async () => {
    if (!target) return;
    setBusy(true); setActionError("");
    const r = await trialApi("post", "trialReschedule", { c: code, scheduleClassId: target._id });
    setBusy(false);
    if (!r.ok) { setActionError(r.message); await load(); setPhase("pick"); return; }
    setInfo((x) => ({ ...x, trial: { ...r.data.session, canChange: true }, status: "booked", movedFromNothing: !trial }));
    setPhase("moved");
  };

  const firstName = info?.firstName;

  return (
    <TwMainLayout fullScreen={true} checkSession={false} userType="skip">
      <TrialLayout details={details} apiClub={club} compact>
        <div className="flex flex-col gap-5 pt-5">
          {phase === "loading" && (
            <>
              <div className="h-8 w-2/3 animate-pulse rounded-xl bg-[#EDE6F0]" />
              <div className="h-[132px] animate-pulse rounded-3xl bg-[#EDE6F0]" />
              <div className="h-[54px] animate-pulse rounded-2xl bg-[#EDE6F0]" />
            </>
          )}

          {phase === "error" && (
            <Card className="flex flex-col gap-4 p-5">
              <h1 className="text-[22px] font-black text-gray-900">This link doesn't work</h1>
              <Alert>{error}</Alert>
              {link && <PrimaryButton onClick={() => navigate("/trial")}>See the classes</PrimaryButton>}
            </Card>
          )}

          {phase === "view" && info?.status === "member" && (
            <Card className="flex flex-col gap-4 p-5">
              <h1 className="text-[22px] font-black text-gray-900">You're a member now</h1>
              <p className="text-[15px] text-gray-600">{info.message || `Sign in to ${clubName} to see and change your bookings.`}</p>
              <PrimaryButton onClick={() => navigate("/")}>Sign in</PrimaryButton>
            </Card>
          )}

          {phase === "view" && info?.status === "booked" && trial && (
            <>
              <div>
                <h1 className="text-[24px] font-black tracking-tight text-gray-900">{firstName ? `Hi ${firstName}, here's your trial` : "Your free trial class"}</h1>
                <p className="mt-1 text-[15px] text-gray-600">See you at {clubName}!</p>
              </div>
              <SessionSummary session={trial} />
              <div className="grid grid-cols-2 gap-3">
                <PrimaryButton tone="soft" onClick={() => downloadIcs(trial, clubName)} className="whitespace-nowrap text-[14px]">{Icons.cal("h-5 w-5")} Add to calendar</PrimaryButton>
                {mapsUrl(trial.location) ? (
                  <a href={mapsUrl(trial.location)} target="_blank" rel="noreferrer" className="flex h-[54px] items-center justify-center gap-2 rounded-2xl bg-[#F3ECF5] text-[15px] font-extrabold text-brown-coffee">
                    {Icons.pin("h-5 w-5")} Directions
                  </a>
                ) : <span />}
              </div>
              {trial.canChange ? (
                <Card className="flex flex-col gap-3 p-4">
                  <div className="text-[16px] font-extrabold text-gray-900">Can't make it?</div>
                  <PrimaryButton onClick={() => { setTarget(null); setActionError(""); setPhase("pick"); }}>Pick another time</PrimaryButton>
                  <PrimaryButton tone="danger" onClick={() => { setActionError(""); setPhase("confirmCancel"); }}>Cancel my trial</PrimaryButton>
                  <p className="text-[13px] text-gray-500">You can change or cancel up to {info.changeCutoffHours || 3} hours before the class.</p>
                </Card>
              ) : (
                <Card className="flex flex-col gap-3 p-4">
                  <div className="text-[16px] font-extrabold text-gray-900">Can't make it?</div>
                  <p className="text-[14px] text-gray-600">Changes close {info.changeCutoffHours || 3} hours before the class. Please let {clubName} know:</p>
                  <ClubContact club={club} />
                </Card>
              )}
            </>
          )}

          {phase === "view" && info?.status && !["booked", "member"].includes(info.status) && (
            <>
              <div>
                <h1 className="text-[24px] font-black tracking-tight text-gray-900">
                  {info.status === "used" ? "You've had your free trial" : "You have no trial booked"}
                </h1>
                <p className="mt-1 text-[15px] text-gray-600">
                  {info.status === "used"
                    ? `Liked it? Ask ${clubName} about joining.`
                    : info.canBook
                      ? "Pick a time that suits you."
                      : `${clubName} isn't taking trial bookings online right now.`}
                </p>
              </div>
              {info.canBook ? (
                <SessionPicker clubLink={clubLink} actionLabel="Book" onPick={(s) => { setTarget(s); setActionError(""); setPhase("confirmMove"); }} />
              ) : (
                <Card className="p-4"><ClubContact club={club} /></Card>
              )}
            </>
          )}

          {phase === "pick" && (
            <>
              <button type="button" onClick={() => setPhase("view")} className="-ml-1 flex w-fit items-center gap-1 text-[15px] font-extrabold text-brown-coffee">
                {Icons.back("h-5 w-5")} Keep my current time
              </button>
              <div>
                <h1 className="text-[24px] font-black tracking-tight text-gray-900">Pick another time</h1>
                {trial && <p className="mt-1 text-[15px] text-gray-600">Now: {trial.className}, {longDay(trial.classDate)} {timeText(trial.classDate)}</p>}
              </div>
              <Alert>{actionError}</Alert>
              <SessionPicker clubLink={clubLink} actionLabel="Move" currentId={trial?._id} startDay={trial ? dayKey(trial.classDate) : ""} onPick={(s) => { setTarget(s); setActionError(""); setPhase("confirmMove"); }} />
            </>
          )}

          {phase === "confirmMove" && target && (
            <>
              <h1 className="text-[24px] font-black tracking-tight text-gray-900">{trial ? "Move your trial to" : "Book your trial for"}</h1>
              <SessionSummary session={target} />
              <Alert>{actionError}</Alert>
              <PrimaryButton tone="coral" busy={busy} onClick={move}>{trial ? "Yes, move my trial" : "Book this class"}</PrimaryButton>
              <PrimaryButton tone="soft" disabled={busy} onClick={() => setPhase(trial ? "pick" : "view")}>Choose another</PrimaryButton>
            </>
          )}

          {phase === "confirmCancel" && trial && (
            <>
              <h1 className="text-[24px] font-black tracking-tight text-gray-900">Cancel your trial?</h1>
              <SessionSummary session={trial} />
              <Alert>{actionError}</Alert>
              <PrimaryButton tone="danger" busy={busy} onClick={cancel}>Yes, cancel my trial</PrimaryButton>
              <PrimaryButton tone="soft" disabled={busy} onClick={() => setPhase("view")}>Keep it</PrimaryButton>
              <button type="button" onClick={() => setPhase("pick")} className="text-[15px] font-extrabold text-brown-coffee">Or pick another time</button>
            </>
          )}

          {phase === "moved" && info?.trial && (
            <>
              <div className="flex flex-col items-center text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#E8F7EE] text-[#1F6B3A]">{Icons.check("h-8 w-8")}</div>
                <h1 className="mt-4 text-[26px] font-black tracking-tight text-gray-900">{info.movedFromNothing ? "You're booked!" : "Your trial has moved"}</h1>
                <p className="mt-1 text-[15px] text-gray-600">We've texted you the new time.</p>
              </div>
              <SessionSummary session={info.trial} />
              <PrimaryButton tone="soft" onClick={() => downloadIcs(info.trial, clubName)}>{Icons.cal("h-5 w-5")} Add to calendar</PrimaryButton>
              <PrimaryButton onClick={() => load()}>Done</PrimaryButton>
            </>
          )}

          {phase === "cancelled" && (
            <>
              <div className="flex flex-col items-center text-center">
                <h1 className="text-[26px] font-black tracking-tight text-gray-900">Your trial is cancelled</h1>
                <p className="mt-1 text-[15px] text-gray-600">Changed your mind? Your free class is still waiting for you.</p>
              </div>
              {info?.canBook && <PrimaryButton tone="coral" onClick={() => load()}>Book another time</PrimaryButton>}
            </>
          )}
        </div>
      </TrialLayout>
    </TwMainLayout>
  );
}
